import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { progressApi } from '../../api/progress'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import Loading from '../../components/common/Loading'
import { getStorageUrl } from '../../utils/constants'
import { FiCheckCircle, FiXCircle, FiBookOpen } from 'react-icons/fi'
import './MyProgress.css'

const formatDate = (value) => {
  if (!value) return null
  return new Date(value).toLocaleDateString('ar-EG', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const MyProgress = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['my-progress'],
    queryFn: () => progressApi.getMyProgress(),
  })

  if (isLoading) {
    return (
      <>
        <Header />
        <Loading fullScreen text="جاري التحميل..." />
        <Footer />
      </>
    )
  }

  const summary = data?.data?.summary
  const courses = data?.data?.courses || []
  const exams = data?.data?.exams || []

  // A student who has watched nothing and sat nothing is not doing badly —
  // they have not started. Showing them a scoreboard of zeros reads as
  // failure on a course they have just paid for.
  const hasActivity = summary?.courses_started > 0 || summary?.exams_sat > 0

  return (
    <div className="my-progress-page bg-gray-900 min-h-screen">
      <Header />
      <main className="my-progress-main">
        <div className="container mx-auto px-4 py-8">
          <h1 className="my-progress-title">مستوايا</h1>

          {error && (
            <p className="my-progress-error">تعذّر تحميل بياناتك. حاول مرة أخرى.</p>
          )}

          {!error && !hasActivity && (
            <div className="my-progress-empty">
              <FiBookOpen className="my-progress-empty-icon" />
              <h2>لم تبدأ بعد</h2>
              <p>افتح أول محاضرة وسيظهر تقدّمك هنا.</p>
              <Link to="/my-courses" className="my-progress-empty-button">
                اذهب إلى دوراتي
              </Link>
            </div>
          )}

          {!error && hasActivity && (
            <>
              <section className="my-progress-summary">
                <div className="my-progress-stat">
                  <span className="my-progress-stat-value">
                    {summary.courses_completed} / {summary.courses_started}
                  </span>
                  <span className="my-progress-stat-label">دورات أنهيتها</span>
                </div>
                <div className="my-progress-stat">
                  <span className="my-progress-stat-value">
                    {summary.exams_passed} / {summary.exams_sat}
                  </span>
                  <span className="my-progress-stat-label">اختبارات نجحت فيها</span>
                </div>
                <div className="my-progress-stat">
                  {/* Null when no exam has been sat: zero is a score, and it
                      has not been earned. */}
                  <span className="my-progress-stat-value">
                    {summary.average_score_percent === null
                      ? '—'
                      : `${summary.average_score_percent}%`}
                  </span>
                  <span className="my-progress-stat-label">متوسط درجاتك</span>
                </div>
              </section>

              {courses.length > 0 && (
                <section className="my-progress-section">
                  <h2 className="my-progress-heading">دوراتي</h2>
                  <div className="my-progress-courses">
                    {courses.map((course) => (
                      <Link
                        key={course.id}
                        to={`/courses/${course.id}`}
                        className="my-progress-course"
                      >
                        {course.thumbnail && (
                          <img
                            src={getStorageUrl(course.thumbnail)}
                            alt={course.title}
                            className="my-progress-course-thumb"
                            loading="lazy"
                          />
                        )}
                        <div className="my-progress-course-body">
                          <h3 className="my-progress-course-title">{course.title}</h3>
                          <div className="my-progress-bar">
                            <div
                              className="my-progress-bar-fill"
                              style={{ width: `${course.percent}%` }}
                            />
                          </div>
                          <div className="my-progress-course-meta">
                            <span>{course.percent}%</span>
                            <span>
                              {course.completed_lectures} / {course.total_lectures} محاضرة
                            </span>
                            {course.last_watched_at ? (
                              <span className="my-progress-course-date">
                                آخر مشاهدة {formatDate(course.last_watched_at)}
                              </span>
                            ) : (
                              <span className="my-progress-course-date">لم تبدأ بعد</span>
                            )}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {exams.length > 0 && (
                <section className="my-progress-section">
                  <h2 className="my-progress-heading">اختباراتي</h2>
                  <div className="my-progress-exams">
                    {exams.map((exam) => (
                      <div key={exam.exam_id} className="my-progress-exam">
                        <div className="my-progress-exam-body">
                          <h3 className="my-progress-exam-title">{exam.title}</h3>
                          {exam.course && (
                            <p className="my-progress-exam-course">{exam.course}</p>
                          )}
                          <p className="my-progress-exam-date">
                            {formatDate(exam.submitted_at)}
                          </p>
                        </div>
                        <div className="my-progress-exam-score">
                          <span
                            className={
                              exam.is_passed
                                ? 'my-progress-exam-percent is-passed'
                                : 'my-progress-exam-percent is-failed'
                            }
                          >
                            {exam.percent}%
                          </span>
                          <span className="my-progress-exam-raw">
                            {exam.score} / {exam.total}
                          </span>
                          <span className="my-progress-exam-verdict">
                            {exam.is_passed ? (
                              <>
                                <FiCheckCircle /> ناجح
                              </>
                            ) : (
                              <>
                                <FiXCircle /> راسب
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default MyProgress
